import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

function fmtMonth(year, month) {
  const d = new Date(year, (month || 1) - 1, 1);
  return d.toLocaleDateString("cs-CZ", { month: "2-digit", year: "numeric" }).replace(".", "").replace(" ", "/");
}

function fmtDateTime(val) {
  if (!val) return "";
  const d = new Date(val);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("cs-CZ");
}

export default function PersonalShiftsHistoryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [year, setYear] = useState("");
  const [month, setMonth] = useState("");
  const [status, setStatus] = useState(""); // default (approved/superseded/canceled)

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const title = useMemo(() => {
    const pc = user?.pharmacyCode ? String(user.pharmacyCode) : "";
    return `Historie plánů směn (pobočka ${pc})`;
  }, [user?.pharmacyCode]);

  const load = async () => {
    if (!user?.firma) return;

    try {
      setError("");
      setLoading(true);

      const params = new URLSearchParams();
      params.set("firma", user.firma);

      // ✅ pobočka se NEPOSÍLÁ – backend ji vynutí z tokenu
      if (year) params.set("year", year);
      if (month) params.set("month", month);
      if (status) params.set("status", status);

      const res = await fetch(`/api/shifts/history?${params.toString()}`, {
        credentials: "include",
      });

      if (!res.ok) {
        const t = await res.text();
        throw new Error(t || `HTTP ${res.status}`);
      }

      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("PersonalShiftsHistoryPage load error:", e);
      setItems([]);
      setError("Nepodařilo se načíst historii plánů směn.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.firma]);

  const openPlan = (planId) => {
    if (!planId) return;
    // ✅ otevře USER/RM plánovač, kde má být načtení přes planId (read-only)
    navigate(`/shifts?planId=${encodeURIComponent(planId)}`);
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-extrabold text-gray-800">{title}</h1>
        <div className="text-gray-500 mt-1">
          Zobrazuje schválené / nahrazené (superseded) / zrušené plány. Vše je read-only.
        </div>
      </div>

      {/* Filtry (bez výběru pobočky) */}
      <div className="bg-white rounded-2xl shadow border p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 items-end">
          <div>
            <div className="text-sm text-gray-700 font-semibold mb-1">Rok</div>
            <input
              className="w-full border rounded-xl px-3 py-2"
              placeholder="např. 2026"
              value={year}
              onChange={(e) => setYear(e.target.value)}
            />
          </div>

          <div>
            <div className="text-sm text-gray-700 font-semibold mb-1">Měsíc</div>
            <input
              className="w-full border rounded-xl px-3 py-2"
              placeholder="1–12"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            />
          </div>

          <div>
            <div className="text-sm text-gray-700 font-semibold mb-1">Status</div>
            <select
              className="w-full border rounded-xl px-3 py-2"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              <option value="">(default) hotové: approved/superseded/canceled</option>
              <option value="approved">approved</option>
              <option value="superseded">superseded</option>
              <option value="canceled">canceled</option>
            </select>
          </div>

          <div className="md:col-span-2 flex justify-end">
            <button
              onClick={load}
              className="w-full md:w-auto px-6 py-2.5 rounded-xl bg-blue-600 text-white font-semibold hover:bg-blue-700"
            >
              Obnovit
            </button>
          </div>
        </div>
      </div>

      {loading && <div className="text-gray-500">Načítám historii...</div>}

      {!loading && error && (
        <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 p-4">{error}</div>
      )}

      {!loading && !error && (
        <div className="text-gray-600 mb-2">Nalezeno: <b>{items.length}</b></div>
      )}

      {!loading && !error && items.length > 0 && (
        <div className="overflow-x-auto bg-white rounded-2xl shadow border">
          <table className="min-w-[900px] w-full text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="text-left px-4 py-3">Měsíc</th>
                <th className="text-left px-4 py-3">Verze</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Schváleno</th>
                <th className="text-left px-4 py-3">Upraveno</th>
                <th className="text-right px-4 py-3">Akce</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it._id} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3 font-semibold text-gray-800">{fmtMonth(it.year, it.month)}</td>
                  <td className="px-4 py-3 text-gray-700">{it.version}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center rounded-full px-2 py-1 text-xs border">
                      {it.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{fmtDateTime(it.approvedAt)}</td>
                  <td className="px-4 py-3 text-gray-700">{fmtDateTime(it.updatedAt)}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-2 justify-end">
                      <button
                        onClick={() => openPlan(it._id)}
                        className="px-4 py-2 rounded-xl border bg-white hover:bg-gray-50 font-semibold"
                      >
                        Otevřít
                      </button>

                      <button
                        onClick={() => navigate(`/personal/shifts-print?planId=${encodeURIComponent(it._id)}`)}
                        className="px-4 py-2 rounded-xl bg-blue-600 text-white hover:bg-blue-700 font-semibold"
                      >
                        Tisk
                      </button>
                    </div>

                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
